import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact";
}

const formatViews = (views: number) => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
};

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const category = getCategory(article.category);
  const link = `/article/${article.slug}`;

  if (variant === "compact") {
    return (
      <article className="group flex gap-4">
        <Link
          to={link}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md border border-background-800"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 猎奇吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col justify-center">
          <Link to={`/archives?category=${article.category}`} className="w-fit text-xs text-primary-400">
            {category?.name}
          </Link>
          <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-primary-300">
            <Link to={link}>{article.title}</Link>
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {article.date} · {article.readTime}
          </p>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors hover:border-primary-500/40 sm:grid-cols-2">
        <Link to={link} className="relative block h-56 overflow-hidden sm:h-full" aria-label={article.title}>
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 猎奇吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent sm:bg-gradient-to-r" />
        </Link>
        <div className="flex flex-col justify-center p-5 md:p-7">
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <Link
              to={`/archives?category=${article.category}`}
              className="rounded-full bg-accent-500/15 px-2.5 py-1 text-accent-300"
            >
              {category?.name}
            </Link>
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatViews(article.views)}
            </span>
          </div>
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-50 transition-colors group-hover:text-primary-300 md:text-2xl">
            <Link to={link}>{article.title}</Link>
          </h3>
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-400">{article.excerpt}</p>
          <Link
            to={link}
            className="mt-5 flex w-fit items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-400 transition-colors hover:text-primary-300"
          >
            阅读全文 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/40">
      <Link to={link} className="relative block h-48 overflow-hidden" aria-label={article.title}>
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 猎奇吃瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-2.5 py-1 text-xs text-primary-300 backdrop-blur">
          {category?.name}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-primary-300">
          <Link to={link}>{article.title}</Link>
        </h3>
        <p className="mt-2.5 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-400">{article.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-800 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" /> {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" /> {article.readTime}
          </span>
        </div>
      </div>
    </article>
  );
}